/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

var isNumber = require( '@stdlib/assert-is-number' ).isPrimitive;
var setEnumerableReadOnly = require( '@stdlib/utils-define-read-only-property' );
var setReadOnly = require( '@stdlib/utils-define-nonenumerable-read-only-property' );
var float64ToFloat16 = require( '@stdlib/number-float64-base-to-float16' );
var format = require( '@stdlib/string-format' );
var hasToPrimitiveSymbolSupport = require( '@stdlib/assert-has-to-primitive-symbol-support' ); // eslint-disable-line id-length
var ToPrimitiveSymbol = require( '@stdlib/symbol-to-primitive' );
var toStr = require( './tostring.js' );
var toJSON = require( './tojson.js' );
var valueOf = require( './valueof.js' ); // eslint-disable-line stdlib/no-redeclare


// MAIN //

/**
* 16-bit half-precision floating-point number constructor.
*
* @constructor
* @param {number} value - numeric value
* @throws {TypeError} must invoke using the `new` keyword
* @throws {TypeError} value must be a number
* @returns {Float16} 16-bit half-precision floating-point number
*
* @example
* var x = new Float16( 5.0 );
* // returns <Float16>
*/
function Float16( value ) {
	if ( !( this instanceof Float16 ) ) {
		throw new TypeError( 'invalid invocation. Constructor must be called with the `new` keyword.' );
	}
	if ( !isNumber( value ) ) {
		throw new TypeError( format( 'invalid argument. Must provide a number. Value: `%s`.', value ) );
	}
	setEnumerableReadOnly( this, 'value', float64ToFloat16( value ) );
	return this;
}

/**
* Constructor name.
*
* @name name
* @memberof Float16
* @readonly
* @type {string}
* @default 'Float16'
*
* @example
* var name = Float16.name;
* // returns 'Float16'
*/
setReadOnly( Float16, 'name', 'Float16' );

/**
* Size (in bytes) of the underlying value.
*
* @name BYTES_PER_ELEMENT
* @memberof Float16
* @type {integer}
* @returns {integer} size in bytes
*
* @example
* var nbytes = Float16.BYTES_PER_ELEMENT;
* // returns 2
*/
setReadOnly( Float16, 'BYTES_PER_ELEMENT', 2 );

/**
* Size (in bytes) of the underlying value.
*
* @name BYTES_PER_ELEMENT
* @memberof Float16.prototype
* @type {integer}
* @returns {integer} size in bytes
*
* @example
* var x = new Float16( 5.0 );
*
* var nbytes = x.BYTES_PER_ELEMENT;
* // returns 2
*/
setReadOnly( Float16.prototype, 'BYTES_PER_ELEMENT', 2 );

/**
* Serializes a half-precision floating-point number as a string.
*
* @name toString
* @memberof Float16.prototype
* @type {Function}
* @returns {string} serialized half-precision floating-point number
*
* @example
* var x = new Float16( 5.0 );
*
* var str = x.toString();
* // returns '5'
*/
setReadOnly( Float16.prototype, 'toString', toStr );

/**
* Serializes a half-precision floating-point number as a JSON object.
*
* ## Notes
*
* -   `JSON.stringify()` implicitly calls this method when stringifying a `Float16` instance.
*
* @name toJSON
* @memberof Float16.prototype
* @type {Function}
* @returns {Object} serialized half-precision floating-point number
*
* @example
* var x = new Float16( 5.0 );
*
* var obj = x.toJSON();
* // returns { 'type': 'Float16', 'value': 5.0 }
*/
setReadOnly( Float16.prototype, 'toJSON', toJSON );

/**
* Converts a half-precision floating-point number to a primitive value.
*
* @name valueOf
* @memberof Float16.prototype
* @type {Function}
* @returns {number} primitive value
*
* @example
* var x = new Float16( 5.0 );
*
* var v = x.valueOf();
* // returns 5.0
*/
setReadOnly( Float16.prototype, 'valueOf', valueOf );

/**
* Returns the primitive value of a half-precision floating-point number.
*
* @name toPrimitive
* @memberof Float16.prototype
* @type {Function}
* @param {string} hint - conversion hint
* @returns {number} primitive value
*
* @example
* var hasSymbol = require( '@stdlib/assert-has-to-primitive-symbol-support' );
* var ToPrimitiveSymbol = require( '@stdlib/symbol-to-primitive' );
*
* var x = new Float16( 5.0 );
*
* var v;
* if ( hasSymbol() ) {
*     v = x[ ToPrimitiveSymbol ]( 'number' );
*     // returns 5.0
* }
*/
if ( hasToPrimitiveSymbolSupport() ) {
	setReadOnly( Float16.prototype, ToPrimitiveSymbol, valueOf );
}


// EXPORTS //

module.exports = Float16;
